import React from 'react';
import { Link } from 'react-router-dom';
import { Database, Wifi, Activity, Zap, Shield, BarChart3, ArrowRight, CheckCircle, Radio, Smartphone, Home, Factory } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveIoTPage: React.FC = () => {
  useSEO('cognitive-iot');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Smart Sensors & Devices",
      description: "Connect and manage a wide range of IoT devices, sensors, and smart equipment with real-time data collection."
    },
    {
      icon: <Wifi className="w-6 h-6" />,
      title: "Connectivity Solutions",
      description: "Seamless connectivity through WiFi, Bluetooth, LoRaWAN, 5G, and other wireless communication protocols."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Real-time Analytics",
      description: "Advanced analytics and machine learning algorithms to process IoT data and generate actionable insights."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security & Privacy",
      description: "End-to-end encryption, secure device authentication, and privacy protection for all IoT communications."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Edge Computing",
      description: "Process data at the edge for faster response times and reduced bandwidth usage with intelligent filtering."
    },
    {
      icon: <Radio className="w-6 h-6" />,
      title: "Device Management",
      description: "Centralized platform for device provisioning, monitoring, updating, and lifecycle management."
    }
  ];

  const iotApplications = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Smart Buildings",
      applications: ["Energy management", "HVAC optimization", "Security systems", "Occupancy monitoring"]
    },
    {
      icon: <Factory className="w-8 h-8" />,
      title: "Industrial IoT",
      applications: ["Predictive maintenance", "Asset tracking", "Quality control", "Process optimization"]
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Consumer IoT",
      applications: ["Smart home automation", "Wearable devices", "Health monitoring", "Connected appliances"]
    }
  ];

  const benefits = [
    "Optimize operational costs",
    "Improve equipment efficiency and uptime",
    "Real-time monitoring and alerts",
    "Predictive maintenance capabilities",
    "Enhanced data-driven decision making",
    "Scalable from prototype to enterprise",
    "Energy consumption optimization",
    "Remote monitoring and control"
  ];

  const processes = [
    {
      step: 1,
      title: "IoT Strategy & Planning",
      description: "Assess your requirements, define use cases, and create a comprehensive IoT implementation strategy."
    },
    {
      step: 2,
      title: "Hardware & Connectivity",
      description: "Select and deploy appropriate sensors, devices, and connectivity solutions for your specific needs."
    },
    {
      step: 3,
      title: "Platform Development",
      description: "Build custom IoT platforms with data processing, analytics, and user-friendly dashboards."
    },
    {
      step: 4,
      title: "Integration & Scaling",
      description: "Integrate with existing systems and scale the solution across your entire organization."
    }
  ];

  const technologies = [
    { category: "Protocols", items: ["MQTT", "CoAP", "HTTP/HTTPS", "WebSocket", "LoRaWAN"] },
    { category: "Connectivity", items: ["WiFi", "Bluetooth", "Zigbee", "5G", "NB-IoT"] },
    { category: "Platforms", items: ["AWS IoT", "Azure IoT", "Google Cloud IoT", "Custom Solutions"] },
    { category: "Hardware", items: ["Arduino", "Raspberry Pi", "ESP32", "Industrial Sensors", "Custom PCBs"] },
    { category: "Analytics", items: ["Time Series DB", "Machine Learning", "Real-time Processing", "Predictive Analytics"] },
    { category: "Security", items: ["TLS/SSL", "Device Certificates", "Secure Boot", "OTA Updates"] }
  ];

  const useCases = [
    {
      title: "Predictive Maintenance",
      description: "Monitor equipment health and predict failures before they occur, reducing downtime and maintenance costs."
    },
    {
      title: "Asset Tracking",
      description: "Real-time location tracking and management of assets across facilities, warehouses, or transportation networks."
    },
    {
      title: "Environmental Monitoring",
      description: "Track air quality, temperature, humidity, and other environmental factors for compliance and optimization."
    },
    {
      title: "Energy Management",
      description: "Optimize energy consumption across facilities with smart metering and automated control systems."
    }
  ];

  return (
    <>
      <ServiceSchema
        serviceName="Cognitive IoT"
        description="Internet of Things solutions connecting devices and enabling smart automation"
        serviceType="Internet of Things"
        url="https://cognitivecrafthub.com/services/cognitive-iot"
        features={[
          "Smart Sensors & Devices",
          "Connectivity Solutions",
          "Real-time Analytics",
          "Security & Privacy",
          "Edge Computing",
          "Device Management"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
      {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Database className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive IoT
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Internet of Things Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Connect the physical and digital worlds with intelligent IoT solutions. 
              Build smart, connected systems that collect data, automate processes, and drive innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">IoT Capabilities</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Complete IoT solutions from device connectivity to data analytics
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">IoT Applications</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Versatile solutions across industries and use cases
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {iotApplications.map((app, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="text-blue-600 dark:text-blue-400 mb-4 flex justify-center">
                  {app.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">{app.title}</h3>
                <ul className="space-y-2">
                  {app.applications.map((application, appIndex) => (
                    <li key={appIndex} className="flex items-center text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                      {application}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">IoT Technologies</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Cutting-edge technologies for robust IoT implementations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{tech.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.items.map((item, itemIndex) => (
                    <span key={itemIndex} className="px-3 py-1 bg-blue-500/20 text-blue-300 dark:text-blue-200 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Popular Use Cases</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Real-world IoT implementations that drive business value
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{useCase.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Benefits of Cognitive IoT</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">IoT Implementation Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Systematic approach to successful IoT deployments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Connect Your World?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Transform your operations with intelligent IoT solutions that bridge 
              the gap between physical assets and digital insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Start IoT Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule IoT Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};